import React, { useCallback, useEffect, useState } from 'react';

import { obterProgressoDoVeiculo, ProgressoSubtarefas as Progresso } from '../../api/subtarefasApi';
import { notify } from '../../shared/feedback/notify';
import './ProgressoSubtarefas.css';

interface ProgressoSubtarefasProps {
  carId: string;
  /** Esconde autoria e data (tela do setor de Eventos: acompanha andamento, não pessoas). */
  ocultarAutoria?: boolean;
  /**
   * Restringe a estas esteiras. Usado na aprovação de horas: a decisão é sobre UMA etapa, e
   * listar as demais mistura serviço de outra etapa com o que está sendo aprovado (decisão do
   * Victor, 03/09/2026). Ausente = veículo inteiro, que é o caso dos modais de consulta.
   */
  apenasEsteiraIds?: string[];
}

const formatarData = (valor: string | null): string | null => {
  if (!valor) return null;
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return null;
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Bloco de leitura "o que já foi feito" (at-89, ADR 0041).
 *
 * Compartilhado pelos modais de placa de todos os papéis, no mesmo molde do
 * `ObservacoesAtendimento` (at-54). É a razão de a feature existir: o associado pergunta o que já
 * fizeram no carro dele, e quem atende precisa responder sem ligar para a oficina.
 *
 * 🔴 Três regras que não podem ser afrouxadas:
 * 1. **Só renderiza quando HÁ sub-tarefas.** Veículo sem catálogo configurado não ganha bloco
 *    vazio (mesma decisão do ObservacoesAtendimento).
 * 2. **Falha de carga NUNCA vira "nada foi feito"** (at-60). Aqui o falso vazio é caro: seria
 *    repassado ao associado como se a oficina não tivesse tocado no veículo.
 * 3. **Sub-tarefa não marcada aparece como PENDENTE**, inclusive em serviço já concluído — nada
 *    é descartado (decisão do Victor, 02/09/2026).
 */
const ProgressoSubtarefas: React.FC<ProgressoSubtarefasProps> = ({
  carId,
  ocultarAutoria,
  apenasEsteiraIds,
}) => {
  const [progresso, setProgresso] = useState<Progresso | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [falhou, setFalhou] = useState(false);

  const carregar = useCallback(async () => {
    if (!carId) return;
    setCarregando(true);
    try {
      const dados = await obterProgressoDoVeiculo(carId);
      setProgresso(dados);
      setFalhou(false);
    } catch (error) {
      // A carga falhou: o estado seguro é `null` + sinal explícito. Deixar a lista vazia aqui
      // produziria exatamente a leitura errada que a feature existe para evitar.
      setProgresso(null);
      setFalhou(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar o que já foi feito neste veículo. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, [carId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (carregando) {
    return (
      <div className="progresso-subtarefas">
        <span className="progresso-subtarefas-carregando">Carregando o que já foi feito...</span>
      </div>
    );
  }

  if (falhou) {
    return (
      <div className="progresso-subtarefas">
        <p className="progresso-subtarefas-erro">
          Não foi possível verificar o que já foi feito neste veículo. Esta lista pode estar
          incompleta — recarregue a página antes de informar o associado.
        </p>
      </div>
    );
  }

  /**
   * ⚠️ Lista defensiva de propósito: um shape inesperado (backend antigo, resposta de outra rota)
   * NÃO pode derrubar a árvore React — sem error boundary, um `.map` de `undefined` aqui apaga o
   * modal inteiro que hospeda este bloco, e o usuário perde a tela toda por causa de um bloco
   * acessório. Medido na at-89, ao plugar o componente no modal do Tempo e SLA.
   */
  const todasAsEsteiras = Array.isArray(progresso?.esteiras) ? progresso!.esteiras : [];
  const esteirasComSubtarefas = apenasEsteiraIds
    ? todasAsEsteiras.filter((e) => apenasEsteiraIds.includes(e.esteiraId))
    : todasAsEsteiras;
  const totalItens = esteirasComSubtarefas.reduce((acc, e) => acc + (e.subtarefas?.length ?? 0), 0);

  // Sem sub-tarefas configuradas para as esteiras deste veículo: nada a mostrar.
  if (!progresso || totalItens === 0) return null;

  const totalConcluidas = esteirasComSubtarefas.reduce(
    (acc, e) => acc + (e.subtarefas ?? []).filter((s) => s.concluida).length,
    0,
  );

  /**
   * 🔴 Etapa que o veículo AINDA NÃO ALCANÇOU não produz pendência.
   *
   * `estadoServico === 'PENDENTE'` significa "planejada, nunca trabalhada" (ADR 0021). A
   * sub-tarefa não marcada ali não é omissão — é serviço futuro. Somá-la ao aviso fazia o Chefe
   * ler "4 não foram feitas" ao aprovar horas de uma etapa onde faltava **uma**, e concluir que
   * o colaborador entregou menos do que entregou.
   */
  const etapaJaComecou = (estado: string) => estado !== 'PENDENTE';

  const pendentesDeVerdade = esteirasComSubtarefas.reduce(
    (acc, e) =>
      acc +
      (etapaJaComecou(e.estadoServico) ? (e.subtarefas ?? []).filter((s) => !s.concluida).length : 0),
    0,
  );

  return (
    <div className="progresso-subtarefas">
      <div className="progresso-subtarefas-cabecalho">
        <span className="progresso-subtarefas-titulo">O que já foi feito</span>
        <span className="progresso-subtarefas-contador">
          {totalConcluidas} de {totalItens}
        </span>
      </div>

      {/* at-89: o total pendente em destaque. O contador acima já diz "1 de 5", mas quem
          aprova horas precisa da leitura direta — e a fração inclui etapas futuras, que NÃO
          são pendência. Este aviso conta só o que já deveria ter sido feito. */}
      {pendentesDeVerdade > 0 && (
        <p className="progresso-subtarefas-pendentes-resumo">
          {pendentesDeVerdade === 1
            ? '1 sub-tarefa de etapa já iniciada não foi marcada como feita.'
            : `${pendentesDeVerdade} sub-tarefas de etapas já iniciadas não foram marcadas como feitas.`}
        </p>
      )}

      {esteirasComSubtarefas.map((esteira) => {
        const comecou = etapaJaComecou(esteira.estadoServico);

        return (
        <div key={esteira.esteiraId} className="progresso-subtarefas-esteira">
          <div className="progresso-subtarefas-esteira-cabecalho">
            <span
              className="progresso-subtarefas-esteira-nome"
              style={esteira.esteiraCor ? { borderLeftColor: esteira.esteiraCor } : undefined}
              title={esteira.esteiraNome ?? undefined}
            >
              {esteira.esteiraNome ?? 'Esteira'}
              {esteira.atual && <span className="progresso-subtarefas-badge-atual">Atual</span>}
              {/* at-89: etapa planejada que o veículo ainda não alcançou. Sem este rótulo, a
                  lista de "Pendente" abaixo dela é lida como serviço em atraso. */}
              {!comecou && (
                <span
                  className="progresso-subtarefas-badge-futura"
                  title="O veículo ainda não passou por esta etapa — estas sub-tarefas são previsão, não pendência."
                >
                  Ainda não iniciada
                </span>
              )}
              {!esteira.planejada && (
                <span
                  className="progresso-subtarefas-badge-fora"
                  title="Esta etapa saiu do planejamento, mas o que já foi executado continua registrado."
                >
                  Fora do plano
                </span>
              )}
            </span>
            <span className="progresso-subtarefas-esteira-contador">
              {(esteira.subtarefas ?? []).filter((s) => s.concluida).length} de{' '}
              {(esteira.subtarefas ?? []).length}
            </span>
          </div>

          <ul className="progresso-subtarefas-lista">
            {(esteira.subtarefas ?? []).map((sub) => {
              const quando = formatarData(sub.concluidoEm);
              // Regra do Victor (12/08/2026): dado de célula/linha nunca quebra em duas linhas —
              // nome e autoria ficam lado a lado, com reticências e `title` no texto longo.
              const detalhe =
                sub.concluida && !ocultarAutoria
                  ? [sub.concluidoPorNome, quando].filter(Boolean).join(' - ')
                  : null;

              return (
                <li
                  key={sub.definicaoId}
                  className={`progresso-subtarefas-item ${
                    sub.concluida ? 'concluida' : 'pendente'
                  }`}
                  title={[sub.nome, detalhe].filter(Boolean).join(' - ')}
                >
                  <span className="progresso-subtarefas-marcador" aria-hidden="true">
                    {sub.concluida ? 'X' : ''}
                  </span>
                  <span className="progresso-subtarefas-nome">{sub.nome ?? 'Sub-tarefa'}</span>
                  {detalhe && <span className="progresso-subtarefas-detalhe">{detalhe}</span>}
                  {/* "Pendente" só onde o serviço já começou. Na etapa futura o rótulo é
                      "Prevista" — o mesmo vocabulário que o planejamento usa para
                      `AtividadeEstado.PENDENTE`. */}
                  {!sub.concluida && (
                    <span
                      className={
                        comecou
                          ? 'progresso-subtarefas-pendente-rotulo'
                          : 'progresso-subtarefas-prevista-rotulo'
                      }
                    >
                      {comecou ? 'Pendente' : 'Prevista'}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
        );
      })}
    </div>
  );
};

export default ProgressoSubtarefas;
